import { CONSULTATION_AUDIO_CONSTRAINTS } from "@/services/managedConsultationMedia.js";

const DEVICE_ID_KEYS = [
  "audioDeviceId",
  "videoDeviceId",
  "selectedAudioDeviceId",
  "selectedVideoDeviceId",
  "microphoneDeviceId",
  "cameraDeviceId",
  "afrimed:selectedAudioDeviceId",
  "afrimed:selectedVideoDeviceId",
  "afrimed:microphoneDeviceId",
  "afrimed:cameraDeviceId",
];

export function isRequestedDeviceUnavailableError(error) {
  const name = String(error?.name || "").trim().toLowerCase();
  const message = String(error?.message || "").trim().toLowerCase();

  return (
    name === "notfounderror" ||
    name === "devicesnotfounderror" ||
    name === "overconstrainederror" ||
    message.includes("requested device not found") ||
    message.includes("device not found") ||
    message.includes("could not start video source")
  );
}

export function isMediaPermissionBlockedError(error) {
  const name = String(error?.name || "").trim();
  return (
    name === "NotAllowedError" ||
    name === "PermissionDeniedError" ||
    name === "SecurityError"
  );
}

export function getFriendlyMediaJoinMessage(error, { hasAudioInput = null } = {}) {
  if (isMediaPermissionBlockedError(error)) {
    return "Camera or microphone access is blocked. Please allow access in your browser settings.";
  }

  if (hasAudioInput === false || isRequestedDeviceUnavailableError(error)) {
    return "No microphone was found. Please connect or allow a microphone, then try again.";
  }

  return error?.message || "The consultation could not start right now.";
}

function isDebugEnabled() {
  if (typeof window === "undefined") return false;
  const hostname = String(window.location?.hostname || "").toLowerCase();
  return (
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname === "staging.afrimed.tech" ||
    hostname.endsWith("--afrimed-staging.netlify.app") ||
    import.meta.env?.MODE === "staging" ||
    import.meta.env?.DEV === true
  );
}

function readStoredDeviceId(keys) {
  if (typeof window === "undefined") return "";

  for (const storage of [window.localStorage, window.sessionStorage]) {
    if (!storage) continue;
    for (const key of keys) {
      try {
        const value = String(storage.getItem(key) || "").trim();
        if (value) return value;
      } catch {
        // ignore inaccessible storage
      }
    }
  }

  return "";
}

function clearStoredDeviceId(deviceId) {
  const cleanDeviceId = String(deviceId || "").trim();
  if (!cleanDeviceId || typeof window === "undefined") return;

  for (const storage of [window.localStorage, window.sessionStorage]) {
    if (!storage) continue;
    for (let index = storage.length - 1; index >= 0; index -= 1) {
      try {
        const key = storage.key(index);
        const value = String(storage.getItem(key) || "");
        if (
          DEVICE_ID_KEYS.includes(key) ||
          value === cleanDeviceId ||
          (key && /audio|video|camera|microphone|device/i.test(key) && value.includes(cleanDeviceId))
        ) {
          storage.removeItem(key);
        }
      } catch {
        // ignore inaccessible storage
      }
    }
  }
}

async function enumerateMediaInputs(mediaDevices) {
  if (!mediaDevices?.enumerateDevices) {
    return { audioInputs: [], videoInputs: [] };
  }

  try {
    const devices = await mediaDevices.enumerateDevices();
    return {
      audioInputs: devices.filter((device) => device.kind === "audioinput"),
      videoInputs: devices.filter((device) => device.kind === "videoinput"),
    };
  } catch {
    return { audioInputs: [], videoInputs: [] };
  }
}

function logVideoDeviceJoin(payload) {
  if (!isDebugEnabled()) return;
  console.warn("[video-device-join]", payload);
}

function buildConstraints({ audioDeviceId = "", videoDeviceId = "", wantVideo = true } = {}) {
  return {
    audio: audioDeviceId
      ? { ...CONSULTATION_AUDIO_CONSTRAINTS, deviceId: { exact: audioDeviceId } }
      : CONSULTATION_AUDIO_CONSTRAINTS,
    video: wantVideo
      ? videoDeviceId
        ? { deviceId: { exact: videoDeviceId } }
        : { facingMode: "user", width: 1280 }
      : false,
  };
}

export async function acquireSafeConsultationStream({
  appointmentId = "",
  route = "",
  preferVideo = true,
} = {}) {
  const mediaDevices = navigator.mediaDevices;
  if (!mediaDevices?.getUserMedia) {
    const error = new Error("Camera and microphone are not available in this browser.");
    logVideoDeviceJoin({
      route,
      appointmentId,
      selectedAudioDeviceId: "",
      selectedVideoDeviceId: "",
      availableAudioInputs: 0,
      availableVideoInputs: 0,
      errorName: error.name,
      errorMessage: error.message,
      fallbackAttempted: false,
    });
    throw error;
  }

  const selectedAudioDeviceId = readStoredDeviceId([
    "audioDeviceId",
    "selectedAudioDeviceId",
    "microphoneDeviceId",
    "afrimed:selectedAudioDeviceId",
    "afrimed:microphoneDeviceId",
  ]);
  const selectedVideoDeviceId = readStoredDeviceId([
    "videoDeviceId",
    "selectedVideoDeviceId",
    "cameraDeviceId",
    "afrimed:selectedVideoDeviceId",
    "afrimed:cameraDeviceId",
  ]);

  const initialDevices = await enumerateMediaInputs(mediaDevices);
  const audioDeviceValid =
    !selectedAudioDeviceId ||
    initialDevices.audioInputs.some((device) => device.deviceId === selectedAudioDeviceId);
  const videoDeviceValid =
    !selectedVideoDeviceId ||
    initialDevices.videoInputs.some((device) => device.deviceId === selectedVideoDeviceId);

  const primaryAudioDeviceId = audioDeviceValid ? selectedAudioDeviceId : "";
  const primaryVideoDeviceId = videoDeviceValid ? selectedVideoDeviceId : "";

  if (!audioDeviceValid) clearStoredDeviceId(selectedAudioDeviceId);
  if (!videoDeviceValid) clearStoredDeviceId(selectedVideoDeviceId);

  try {
    const stream = await mediaDevices.getUserMedia(
      buildConstraints({
        audioDeviceId: primaryAudioDeviceId,
        videoDeviceId: primaryVideoDeviceId,
        wantVideo: preferVideo,
      })
    );
    logVideoDeviceJoin({
      route,
      appointmentId,
      selectedAudioDeviceId,
      selectedVideoDeviceId,
      availableAudioInputs: initialDevices.audioInputs.length,
      availableVideoInputs: initialDevices.videoInputs.length,
      errorName: "",
      errorMessage: "",
      fallbackAttempted: !audioDeviceValid || !videoDeviceValid,
    });
    return stream;
  } catch (primaryError) {
    const devices = await enumerateMediaInputs(mediaDevices);
    const hasAudioInput = devices.audioInputs.length > 0;
    const hasVideoInput = devices.videoInputs.length > 0;
    const fallbackAttempted =
      isRequestedDeviceUnavailableError(primaryError) ||
      (!hasVideoInput && preferVideo);

    logVideoDeviceJoin({
      route,
      appointmentId,
      selectedAudioDeviceId,
      selectedVideoDeviceId,
      availableAudioInputs: devices.audioInputs.length,
      availableVideoInputs: devices.videoInputs.length,
      errorName: primaryError?.name || "",
      errorMessage: primaryError?.message || "",
      fallbackAttempted,
    });

    if (isMediaPermissionBlockedError(primaryError)) {
      throw new Error(getFriendlyMediaJoinMessage(primaryError));
    }

    if (!hasAudioInput) {
      throw new Error(getFriendlyMediaJoinMessage(primaryError, { hasAudioInput: false }));
    }

    if (selectedAudioDeviceId) clearStoredDeviceId(selectedAudioDeviceId);
    if (selectedVideoDeviceId) clearStoredDeviceId(selectedVideoDeviceId);

    try {
      return await mediaDevices.getUserMedia(
        buildConstraints({
          wantVideo: preferVideo && hasVideoInput,
        })
      );
    } catch (fallbackError) {
      if (preferVideo && hasAudioInput && isRequestedDeviceUnavailableError(fallbackError)) {
        return mediaDevices.getUserMedia(buildConstraints({ wantVideo: false }));
      }
      throw new Error(
        getFriendlyMediaJoinMessage(fallbackError, {
          hasAudioInput,
        })
      );
    }
  }
}

export function stopConsultationStream(stream) {
  stream?.getTracks?.().forEach((track) => {
    try {
      track.stop();
    } catch {
      // ignore
    }
  });
}
